import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { fmtDate } from '../../dateUtil';
import Transactions from './Transactions';

const STATUS_STYLES = {
  draft:    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  sent:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  complete: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  archived: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
};

function SubTabs({ tab, setTab }) {
  return (
    <div className="flex gap-1 mb-4 overflow-x-auto border-b border-slate-200 dark:border-slate-700">
      {[['deals', 'Deals'], ['transactions', 'Transactions']].map(([id, label]) => (
        <button
          key={id}
          onClick={() => setTab(id)}
          className={`shrink-0 whitespace-nowrap px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === id
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function Deals() {
  const [subTab, setSubTab] = useState('deals');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    api.get('/admin/deals').then(setDeals).finally(() => setLoading(false));
  }, []);

  const filtered = deals.filter(d => {
    if (statusFilter && d.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.name?.toLowerCase().includes(q) ||
      d.seller_account?.toLowerCase().includes(q)
    );
  });

  if (subTab === 'transactions') {
    return (
      <div>
        <SubTabs tab={subTab} setTab={setSubTab} />
        <Transactions />
      </div>
    );
  }

  if (loading) return (
    <div>
      <SubTabs tab={subTab} setTab={setSubTab} />
      <div className="flex items-center justify-center h-48">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <SubTabs tab={subTab} setTab={setSubTab} />
      <div className="flex gap-3">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by deal name or account…"
          className="w-full max-w-sm text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
        >
          <option value="">All statuses</option>
          {['draft', 'sent', 'complete', 'archived'].map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50">
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Deal</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Seller</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
              <th className="py-2.5 px-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Cars</th>
              <th className="py-2.5 px-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Bids</th>
              <th className="py-2.5 px-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Buyers</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">End Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {d.name || <span className="text-slate-400 italic">Unnamed #{d.id}</span>}
                  </p>
                </td>
                <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                  {d.seller_account || <span className="text-slate-400">—</span>}
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[d.status] || STATUS_STYLES.draft}`}>
                    {d.status}
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-slate-600 dark:text-slate-300">{d.car_count}</td>
                <td className="py-3 px-4 text-center text-sm text-slate-600 dark:text-slate-300">{d.bid_count}</td>
                <td className="py-3 px-4 text-center text-sm text-slate-600 dark:text-slate-300">{d.buyer_count}</td>
                <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400">
                  {d.end_date ? fmtDate(d.end_date) : '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-slate-400">
                  {search || statusFilter ? 'No matching deals' : 'No deals yet'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {deals.length === 500 && (
          <p className="text-xs text-center text-slate-400 py-2 border-t border-slate-100 dark:border-slate-800">
            Showing first 500 deals
          </p>
        )}
      </div>
    </div>
  );
}
